<script setup lang="ts">
import { computed, ref } from 'vue'
import { get, post, put } from '@/api'

definePage({
  meta: {
    title: '하이퍼 스피드 - 고객사 관리',
    action: 'read',
    subject: 'hyper-speed',
  },
})

// 고객사 인터페이스
interface HyperSpeedClient {
  id: number | null
  company: string | null
  client_contact_name: string | null
  client_contact_tel: string | null
  account_manager: string | null
  sales_stage: string | null
  contract: string | null
  billing_cycle: string | null
  setting_fee: string | null
  partner_agency: string | null
  hyper_speed_config_target: string | { target: string; enabled: boolean } | null
  created_at?: string
  updated_at?: string
}

// 초기 고객사 모델
const getInitialClient = (): HyperSpeedClient => ({
  id: null,
  company: null,
  client_contact_name: null,
  client_contact_tel: null,
  account_manager: null,
  sales_stage: null,
  contract: null,
  billing_cycle: null,
  setting_fee: null,
  partner_agency: null,
  hyper_speed_config_target: null,
})

// 상태
const selectedClient = ref<HyperSpeedClient>(getInitialClient())
const clients = ref<HyperSpeedClient[]>([])
const loading = ref(false)
const saving = ref(false)

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 영업 단계 옵션
const salesStageOptions = [
  { value: null, title: '세일즈 진행 단계' },
  { value: 'ready_for_sales', title: '영업예정' },
  { value: 'plan_to_meeting', title: '미팅 및 협의' },
  { value: 'install_script', title: '스크립트 설치' },
  { value: 'test_progress', title: '테스트중' },
  { value: 'sending_estimate', title: '견적서 발송' },
  { value: 'exit_on_testing', title: '테스트 이탈' },
  { value: 'contract_progress', title: '계약진행' },
  { value: 'contract_success', title: '계약완료' },
  { value: 'test_finished', title: '테스트 종료' },
  { value: 'contract_finished', title: '계약만료' },
  { value: 'contract_renewal_progress', title: '재계약 협의' },
]

// 결제 주기 옵션
const billingCycleOptions = [
  { value: null, title: '계약전' },
  { value: 'yearly', title: '매년' },
  { value: 'monthly', title: '매월' },
]

// 테이블 헤더
const headers = [
  { title: '회사명', key: 'company' },
  { title: '담당자', key: 'client_contact_name' },
  { title: '연락처', key: 'client_contact_tel' },
  { title: '진행 단계', key: 'sales_stage' },
  { title: '계약사항', key: 'contract', width: '200px' },
  { title: '결제 주기', key: 'billing_cycle' },
  { title: '셋팅비', key: 'setting_fee' },
  { title: '대행사', key: 'partner_agency' },
  { title: '생성일', key: 'created_at' },
]

// 발행코드 관련 computed
const targetCode = computed(() => {
  const target = selectedClient.value.hyper_speed_config_target
  if (target && typeof target === 'object')
    return target.target

  return target as string | null
})

const serviceEnabled = computed({
  get: () => {
    const target = selectedClient.value.hyper_speed_config_target
    if (target && typeof target === 'object')
      return target.enabled

    return false
  },
  set: (value: boolean) => {
    const target = selectedClient.value.hyper_speed_config_target
    if (target && typeof target === 'object')
      target.enabled = value
  },
})

const isExistingTarget = computed(() => {
  const target = selectedClient.value.hyper_speed_config_target

  return target && typeof target === 'object'
})

// 영업 단계 라벨 반환
const getSalesStageLabel = (value: string | null) => {
  const option = salesStageOptions.find(opt => opt.value === value)

  return option?.title || value || '-'
}

// 결제 주기 라벨 반환
const getBillingCycleLabel = (value: string | null) => {
  const option = billingCycleOptions.find(opt => opt.value === value)

  return option?.title || value || '-'
}

// 날짜 포맷
const formatDate = (dateStr: string | undefined) => {
  if (!dateStr)
    return '-'

  return new Date(dateStr).toLocaleDateString('ko-KR')
}

// 고객사 목록 로드
const loadClients = async () => {
  loading.value = true
  try {
    const { success, data } = await get<{ data: HyperSpeedClient[] } | HyperSpeedClient[]>('/hyper-speed-client')
    if (success && data) {
      // Laravel Resource Collection은 {data: [...]} 형식으로 응답
      clients.value = Array.isArray(data) ? data : (data.data || [])
    }
  }
  catch (error) {
    console.error('Failed to load clients:', error)
    showNotification('고객사 목록을 불러오는데 실패했습니다.', 'error')
  }
  finally {
    loading.value = false
  }
}

// 고객사 선택
const selectClient = (client: HyperSpeedClient) => {
  selectedClient.value = JSON.parse(JSON.stringify(client))
}

// 고객사 저장
const saveClient = async () => {
  if (!selectedClient.value.company) {
    showNotification('업체명을 입력해주세요.', 'warning')

    return
  }

  saving.value = true
  try {
    const payload = JSON.parse(JSON.stringify(selectedClient.value))

    // hyper_speed_config_target 처리
    if (payload.hyper_speed_config_target && typeof payload.hyper_speed_config_target !== 'string') {
      payload.enabled = payload.hyper_speed_config_target.enabled
      payload.hyper_speed_config_target = payload.hyper_speed_config_target.target
    }
    else {
      payload.enabled = true
    }

    const isUpdate = selectedClient.value.id !== null
    const url = isUpdate ? `/hyper-speed-client/${selectedClient.value.id}` : '/hyper-speed-client'

    const { success, error } = isUpdate
      ? await put(url, payload)
      : await post(url, payload)

    if (success) {
      showNotification(isUpdate ? '고객사 정보가 수정되었습니다.' : '고객사가 등록되었습니다.')
      await loadClients()
      if (!isUpdate)
        resetForm()
    }
    else {
      showNotification(error?.response?.data?.message || '저장에 실패했습니다.', 'error')
    }
  }
  catch (error: any) {
    showNotification(error?.message || '저장에 실패했습니다.', 'error')
  }
  finally {
    saving.value = false
  }
}

// 폼 초기화
const resetForm = () => {
  selectedClient.value = getInitialClient()
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 초기 로드
onMounted(() => {
  loadClients()
})
</script>

<template>
  <VRow>
    <!-- 고객사 등록/수정 폼 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>고객사 등록 및 수정</VCardTitle>
        <VCardText>
          <!-- 업체 정보 -->
          <div class="text-h6 mb-4">
            업체 정보
          </div>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedClient.company"
                label="업체명"
                placeholder="업체명을 입력하세요"
                :disabled="selectedClient.id !== null"
                required
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedClient.client_contact_name"
                label="담당자"
                placeholder="담당자명"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedClient.client_contact_tel"
                label="담당자 연락처"
                placeholder="연락처"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VSelect
                v-model="selectedClient.sales_stage"
                :items="salesStageOptions"
                item-title="title"
                item-value="value"
                label="영업 단계"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <template v-if="isExistingTarget">
                <VTextField
                  :model-value="targetCode"
                  label="발행코드"
                  disabled
                />
                <VSwitch
                  v-model="serviceEnabled"
                  label="서비스 상태"
                  color="primary"
                  class="mt-2"
                />
              </template>
              <VTextField
                v-else
                v-model="selectedClient.hyper_speed_config_target"
                label="발행코드"
                placeholder="발행코드 입력"
              />
            </VCol>
          </VRow>

          <VDivider class="my-6" />

          <!-- 계약 정보 -->
          <div class="text-h6 mb-4">
            계약 정보
          </div>

          <VRow>
            <VCol cols="12">
              <VTextarea
                v-model="selectedClient.contract"
                label="진행사항"
                placeholder="진행사항을 입력하세요"
                rows="3"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VRadioGroup
                v-model="selectedClient.billing_cycle"
                label="결제 주기"
                inline
              >
                <VRadio
                  v-for="option in billingCycleOptions"
                  :key="option.value || 'null'"
                  :label="option.title"
                  :value="option.value"
                />
              </VRadioGroup>
            </VCol>
          </VRow>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedClient.setting_fee"
                label="셋팅비"
                placeholder="셋팅비용"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedClient.partner_agency"
                label="대행사"
                placeholder="대행사명"
              />
            </VCol>
          </VRow>

          <VRow class="mt-4">
            <VCol>
              <VBtn
                v-if="selectedClient.id"
                variant="outlined"
                class="me-2"
                @click="resetForm"
              >
                초기화
              </VBtn>
              <VBtn
                color="primary"
                :loading="saving"
                @click="saveClient"
              >
                {{ selectedClient.id ? '수정하기' : '저장하기' }}
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 고객사 목록 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>고객사 목록</VCardTitle>
        <VCardText>
          <VDataTable
            :headers="headers"
            :items="clients"
            :loading="loading"
            hover
            density="compact"
            class="cursor-pointer"
            @click:row="(_event: Event, { item }: { item: HyperSpeedClient }) => selectClient(item)"
          >
            <template #item.sales_stage="{ item }">
              {{ getSalesStageLabel(item.sales_stage) }}
            </template>

            <template #item.billing_cycle="{ item }">
              {{ getBillingCycleLabel(item.billing_cycle) }}
            </template>

            <template #item.contract="{ item }">
              <span
                class="text-truncate d-inline-block"
                style="max-width: 200px;"
              >
                {{ item.contract || '-' }}
              </span>
            </template>

            <template #item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <template #item.client_contact_name="{ item }">
              {{ item.client_contact_name || '-' }}
            </template>

            <template #item.client_contact_tel="{ item }">
              {{ item.client_contact_tel || '-' }}
            </template>

            <template #item.setting_fee="{ item }">
              {{ item.setting_fee || '-' }}
            </template>

            <template #item.partner_agency="{ item }">
              {{ item.partner_agency || '-' }}
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
.cursor-pointer :deep(tbody tr) {
  cursor: pointer;
}
</style>
